/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        MeteoWidget.tsx
 * @description Widget enrichi affichant les conditions meteo en temps reel
 *              Destine au header de l'application avec infos hiver
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState } from 'react';
import { useMetar, getWeatherIcon, getAlertColorClass, formatVisibility } from '@/hooks/useMetar';
import { ArrowPathIcon, ExclamationTriangleIcon, CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

// Aeroports disponibles
const AIRPORTS = [
  { icao: 'LFPO', name: 'Paris-Orly', iata: 'ORY' },
  { icao: 'LFPG', name: 'Paris-CDG', iata: 'CDG' },
];

interface MeteoWidgetProps {
  icao?: string;
  className?: string;
}

/**
 * Widget Meteo enrichi pour le header
 */
export function MeteoWidget({ icao = 'LFPO', className }: MeteoWidgetProps) {
  const [selectedIcao, setSelectedIcao] = useState(icao);
  const [showDropdown, setShowDropdown] = useState(false);
  const { data: metar, isLoading, isError, refetch } = useMetar(selectedIcao);
  const [showTooltip, setShowTooltip] = useState(false);

  const currentAirport = AIRPORTS.find(a => a.icao === selectedIcao) || AIRPORTS[0];
  const alertLevel = metar?.winter_indicators?.alert_level ?? 'normal';

  if (isLoading) {
    return (
      <div className={clsx('flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg animate-pulse', className)}>
        <div className="w-6 h-6 bg-gray-300 rounded" />
        <div className="w-24 h-4 bg-gray-300 rounded" />
      </div>
    );
  }

  if (isError || !metar) {
    return (
      <div className={clsx('flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-lg', className)}>
        <span className="text-gray-500 text-sm">Meteo N/A</span>
        <button onClick={() => refetch()} className="p-1 hover:bg-gray-200 rounded">
          <ArrowPathIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>
    );
  }

  const weatherIcon = getWeatherIcon(metar);
  const hasRisks = metar.winter_indicators?.risque_givrage ||
                   metar.winter_indicators?.risque_verglas ||
                   metar.winter_indicators?.neige_active ||
                   metar.snoclo;

  return (
    <div
      className={clsx('relative', className)}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Widget principal */}
      <div className={clsx(
        'flex items-center gap-2 px-2 py-1 rounded-lg border transition-all',
        alertLevel === 'normal' && 'bg-green-50 border-green-200',
        alertLevel === 'vigilance' && 'bg-yellow-50 border-yellow-300',
        alertLevel === 'warning' && 'bg-amber-50 border-amber-300',
        alertLevel === 'critique' && 'bg-red-50 border-red-300'
      )}>
        {/* Liste deroulante aeroport */}
        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setShowDropdown(!showDropdown); }}
            className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded text-xs font-medium hover:bg-gray-50"
          >
            <span>{currentAirport.iata}</span>
            <ChevronDownIcon className="w-3 h-3" />
          </button>
          {showDropdown && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded shadow-lg border border-gray-200 py-1 min-w-[120px]">
              {AIRPORTS.map((airport) => (
                <button
                  key={airport.icao}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedIcao(airport.icao);
                    setShowDropdown(false);
                  }}
                  className={clsx(
                    'w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 flex items-center justify-between',
                    airport.icao === selectedIcao && 'bg-blue-50 text-blue-700'
                  )}
                >
                  <span>{airport.name}</span>
                  <span className="text-gray-400">{airport.iata}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Icone + Temperature */}
        <div className="flex items-center gap-1">
          <span className="text-xl">{weatherIcon}</span>
          <span className="text-base font-bold">{metar.temperature ?? '--'}°</span>
        </div>

        {/* Vent */}
        <div className="text-xs text-gray-600 border-l pl-2">
          <span>💨 {metar.wind_speed ?? 0}kt</span>
          {metar.wind_gust && <span className="text-amber-600 ml-1">G{metar.wind_gust}</span>}
        </div>

        {/* Visibilite */}
        <div className="text-xs text-gray-600">
          👁 {formatVisibility(metar.visibility)}
        </div>

        {/* Separateur */}
        <div className="border-l h-6" />

        {/* Risques */}
        <div className="flex items-center gap-1 text-xs">
          {metar.winter_indicators?.risque_givrage && (
            <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-700">Giv</span>
          )}
          {metar.winter_indicators?.risque_verglas && (
            <span className="px-1 py-0.5 rounded bg-cyan-100 text-cyan-700">Verg</span>
          )}
          {metar.winter_indicators?.neige_active && (
            <span className="px-1 py-0.5 rounded bg-indigo-100 text-indigo-700">Neige</span>
          )}
          {metar.snoclo && (
            <span className="px-1 py-0.5 rounded bg-red-100 text-red-700 font-bold animate-pulse">SNOCLO</span>
          )}
          {!hasRisks && (
            <span className="text-green-600 flex items-center gap-0.5">
              <CheckCircleIcon className="w-3.5 h-3.5" />
              OK
            </span>
          )}
        </div>

        {/* Alerte */}
        {(alertLevel === 'warning' || alertLevel === 'critique') && (
          <ExclamationTriangleIcon className="w-4 h-4 text-red-600 animate-pulse" />
        )}

        {/* Refresh */}
        <button
          onClick={(e) => { e.stopPropagation(); refetch(); }}
          className="p-1 hover:bg-black/10 rounded"
          title="Rafraîchir"
        >
          <ArrowPathIcon className="w-3.5 h-3.5" />
        </button>

        {/* Heure METAR */}
        <span className="text-xs text-gray-500">{metar.metar_tranche}</span>
      </div>

      {/* Tooltip enrichi */}
      {showTooltip && (
        <div className="absolute top-full right-0 mt-2 z-50 w-80 p-3 bg-white rounded-lg shadow-xl border border-gray-200 text-sm">
          <MeteoTooltipContent metar={metar} alertLevel={alertLevel} />
        </div>
      )}
    </div>
  );
}

/**
 * Contenu du tooltip avec details meteo
 */
function MeteoTooltipContent({
  metar,
  alertLevel,
}: {
  metar: NonNullable<ReturnType<typeof useMetar>['data']>;
  alertLevel: string;
}) {
  const spreadTempDew = metar.winter_indicators?.spread_temp_dewpoint ?? null;
  const alertMessage = metar.winter_indicators?.alert_message ?? '';

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex justify-between items-center pb-2 border-b">
        <div>
          <span className="font-semibold">{metar.airport_name}</span>
          <span className="text-xs text-gray-500 ml-2">{metar.icao}</span>
        </div>
        <span className="text-2xl">{getWeatherIcon(metar)}</span>
      </div>

      {/* Alerte */}
      {alertLevel !== 'normal' && (
        <div className={clsx('p-2 rounded text-xs', getAlertColorClass(alertLevel))}>
          <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
          {alertMessage}
        </div>
      )}

      {/* Grille données */}
      <div className="grid grid-cols-4 gap-2 text-xs">
        <div className="bg-gray-50 p-1.5 rounded text-center">
          <div className="text-gray-500">Temp</div>
          <div className="font-bold text-base">{metar.temperature}°</div>
        </div>
        <div className={clsx('p-1.5 rounded text-center', spreadTempDew !== null && spreadTempDew <= 3 ? 'bg-cyan-50' : 'bg-gray-50')}>
          <div className="text-gray-500">Rosée</div>
          <div className="font-bold">{metar.dewpoint}° <span className="text-xs font-normal">Δ{spreadTempDew}</span></div>
        </div>
        <div className="bg-gray-50 p-1.5 rounded text-center">
          <div className="text-gray-500">Humid</div>
          <div className="font-bold">{metar.humidity}%</div>
        </div>
        <div className="bg-gray-50 p-1.5 rounded text-center">
          <div className="text-gray-500">QNH</div>
          <div className="font-bold">{metar.qnh}</div>
        </div>
      </div>

      {/* Vent */}
      <div className="flex justify-between items-center bg-gray-50 p-2 rounded text-xs">
        <span className="text-gray-500">Vent</span>
        <span>
          {metar.wind_dir_text} {metar.wind_speed}kt
          {metar.wind_gust && <span className="text-amber-600 ml-1">(raf. {metar.wind_gust}kt)</span>}
        </span>
      </div>

      {/* Visi */}
      <div className="flex justify-between items-center bg-gray-50 p-2 rounded text-xs">
        <span className="text-gray-500">Visibilité</span>
        <span>{formatVisibility(metar.visibility)}</span>
      </div>

      {/* Risques */}
      <div className="flex items-center justify-between pt-2 border-t">
        <span className="text-xs text-gray-500">Risques hiver</span>
        <div className="flex gap-1 text-xs">
          {metar.winter_indicators?.risque_givrage && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Givrage</span>}
          {metar.winter_indicators?.risque_verglas && <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700">Verglas</span>}
          {metar.winter_indicators?.neige_active && <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">Neige</span>}
          {metar.snoclo && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">SNOCLO</span>}
          {!metar.winter_indicators?.risque_givrage && !metar.winter_indicators?.risque_verglas &&
           !metar.winter_indicators?.neige_active && !metar.snoclo && (
            <span className="text-green-600">Aucun</span>
          )}
        </div>
      </div>

      {/* Statut */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">Opérations</span>
        <span className={clsx(
          'text-xs px-2 py-0.5 rounded font-medium',
          metar.winter_indicators?.conditions_operables ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        )}>
          {metar.winter_indicators?.conditions_operables ? '✓ Opérables' : '✗ Non opérables'}
        </span>
      </div>

      {/* Footer */}
      <div className="flex justify-between text-xs text-gray-400 pt-2 border-t">
        <span>🌅 {metar.sunrise} - 🌇 {metar.sunset}</span>
        <span>{metar.metar_tranche}</span>
      </div>
    </div>
  );
}

export default MeteoWidget;
